import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ScrollText, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { InfoPanel } from "../components/intake/fields";
import { QuickExit } from "../components/intake/QuickExit";
import { sendMediationReferral } from "../lib/api";

const Q = [
  {
    q: "What is a MIAM?",
    a: "A Mediation Information and Assessment Meeting (MIAM) is a first meeting with a trained mediator. It explains how mediation works and helps you decide whether mediation, rather than going straight to court, could help you resolve matters about children or finances.",
  },
  {
    q: "Do I have to attend one?",
    a: "Before making certain family court applications, an applicant will usually need to attend a MIAM, unless an exemption or another exception applies. Attending a MIAM does not mean you have to take part in mediation.",
  },
  {
    q: "When might an exemption apply?",
    a: "Exemptions may apply in some situations — for example where there is evidence of domestic abuse, where a child's welfare is at risk, in cases of urgency, or where mediation is not suitable. The information you provide may be relevant to an exemption, but further information or evidence may be required.",
  },
  {
    q: "What happens after a MIAM?",
    a: "If mediation is suitable and everyone agrees, the mediator can help you work towards an agreement. If mediation is not suitable or does not resolve matters, the mediator can usually provide signed confirmation that a MIAM took place, which may be needed for a court application.",
  },
];

export default function Miam() {
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const valid = f.first_name && f.last_name && f.email;

  const submit = async () => {
    setBusy(true);
    try {
      const res = await sendMediationReferral(f);
      toast.success(`Referral request sent (ref ${res.reference}).`);
      setSent(true);
    } catch (e) {
      toast.error("Could not send your request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <QuickExit />
      <header className="border-b border-border bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <p className="font-serif text-xl text-brand-black">Family Court Guide</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-brand-gold hover:text-brand-black mb-6" data-testid="miam-back">
          <ArrowLeft className="h-4 w-4" /> Back to the form
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <ScrollText className="h-7 w-7 text-brand-gold" strokeWidth={1.25} />
          <h1 className="font-serif text-3xl text-brand-black">MIAM and mediation — in plain English</h1>
        </div>
        <p className="text-sm text-brand-darkgrey leading-relaxed mb-8">
          Mediation can be a calmer, quicker and less expensive way to sort out arrangements after a
          separation. This page explains what a MIAM is, when you may need one, and how to ask us for a
          referral.
        </p>

        <div className="space-y-4 mb-10">
          {Q.map((item, i) => (
            <div key={item.q} className="border border-border bg-white p-5" data-testid={`miam-faq-${i}`}>
              <h3 className="font-serif text-lg text-brand-black mb-1">{item.q}</h3>
              <p className="text-sm text-brand-darkgrey leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>

        <InfoPanel tone="grey">
          This information is a general guide and is not legal advice. Whether a MIAM or an exemption applies
          depends on your circumstances.
        </InfoPanel>

        <div className="mt-10 border border-brand-gold/60 bg-white p-6" data-testid="mediation-referral-form">
          <h2 className="font-serif text-2xl text-brand-black mb-2">Request a mediation referral</h2>
          {sent ? (
            <div className="flex items-start gap-3 py-4" data-testid="mediation-referral-success">
              <CheckCircle2 className="h-6 w-6 text-brand-gold shrink-0" strokeWidth={1.5} />
              <p className="text-sm text-brand-darkgrey">
                Thank you — we have received your mediation referral request. We will pass your details to a
                mediation service, and a mediator will typically contact you within 2 business days using the
                contact information you provided. We have also sent a confirmation to your email.
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-brand-darkgrey mb-5">
                Leave your details and we will arrange for a mediation service to contact you about a MIAM.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">First name</Label>
                  <Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="mediation-first-name" className="rounded-sm focus-visible:ring-brand-gold" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Last name</Label>
                  <Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="mediation-last-name" className="rounded-sm focus-visible:ring-brand-gold" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Email address</Label>
                  <Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="mediation-email" className="rounded-sm focus-visible:ring-brand-gold" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Telephone number (optional)</Label>
                  <Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="mediation-phone" className="rounded-sm focus-visible:ring-brand-gold" />
                </div>
              </div>
              <div className="space-y-2 mt-4">
                <Label className="text-sm font-semibold">Anything you would like us to know (optional)</Label>
                <Textarea value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="mediation-message" className="rounded-sm focus-visible:ring-brand-gold" />
              </div>
              <Button onClick={submit} disabled={!valid || busy} data-testid="mediation-submit" className="mt-5 bg-brand-black text-white hover:bg-brand-gold rounded-sm px-6">
                {busy ? "Sending…" : "Request a referral"}
              </Button>
            </>
          )}
        </div>
      </main>

      <footer className="border-t border-border bg-white mt-10">
        <div className="max-w-3xl mx-auto px-6 py-6 text-xs text-muted-foreground flex flex-wrap gap-4 justify-between">
          <span>© {new Date().getFullYear()} Family Court Guide. This service is privately funded.</span>
          <Link to="/privacy" className="hover:text-brand-gold underline underline-offset-2">Privacy notice</Link>
        </div>
      </footer>
    </div>
  );
}
